import { z } from 'zod';
import { messageSchema } from './message.js';
import { DEFAULT_HOLD_MUSIC, DEFAULT_RINGBACK, DEFAULT_RINGTONE, DEFAULT_SOUND, notificationSoundSchema, holdMusicSchema, ringbackSchema, ringtoneSchema, } from './sounds.js';
export const notifyLevelGlobalSchema = z.enum(['all', 'mentions', 'none']);
/** Miert kapta a felhasznalo az ertesitest - a felulet ezt mutatja meg. */
export const notifyReasonSchema = z.enum([
    'mention',
    'keyword',
    'direct',
    'channel',
    'thread',
    /** Behivtak egy olelesbe, de nem vette fel - lasd `huddle_missed` altipus. */
    'missed_call',
    /** Oleles indult a csatornaban, amibe be lehet lepni. */
    'huddle',
    /** VIP-nek jelolt kollega irt - lasd `vipUserIds`. */
    'vip',
]);
export const notificationPrefsSchema = z.object({
    /** MUNKAIDOBEN mirol szoljunk. */
    globalLevel: notifyLevelGlobalSchema,
    /**
     * MUNKAIDON KIVUL mirol szoljunk.
     *
     * A munkaido-sav a felhasznalo profiljaban van (`presence-status.ts`), mert
     * ugyanaz dont a "munkaidon kivul" statuszrol is. Alapbol `none`: este
     * csak az szol, amit a felhasznalo kifejezetten "mindig"-re allitott.
     */
    afterHoursLevel: notifyLevelGlobalSchema,
    keywords: z.array(z.string()),
    /**
     * A kozvetlen uzenet MINDIG szol: munkaidon kivul is, es akkor is, ha a
     * szint "semmi". A nemitott DM kivetel - amit elhallgattattal, az hallgat.
     */
    directAlways: z.boolean(),
    /**
     * VIP kollegak: amit ok irnak (DM, emlites, kozos csatorna), arrol mindig
     * szolunk, munkaidon kivul is. A nemitott csatorna itt is kivetel.
     */
    vipUserIds: z.array(z.string()),
    soundEnabled: z.boolean(),
    /**
     * Hangero SZAZALEKBAN, minden sajat hangunkra.
     *
     * Eddig nem letezett: minden hang beegetett szinten szolt, es a felhasznalo
     * kezeben csak a "szoljon vagy ne" volt. Aki halkabban szerette volna,
     * annak csak a kikapcsolas maradt.
     *
     * Miert egesz szazalek es nem 0-1 kozotti tort? Mert ez a szam vegigmegy az
     * adatbazison, a JSON-on es egy csuszkan; egesz szamnal nincs kerekitesi
     * elteres a ket veg kozott.
     *
     * A SZORZO, nem abszolut ertek: a hangok egymashoz kepesti aranya megmarad
     * (a csengohang hangosabb, a varakozo zene halkabb), csak a szint valtozik.
     */
    soundVolume: z.number().int().min(0).max(100),
    soundName: notificationSoundSchema,
    ringtone: ringtoneSchema,
    /** Amit MI hallunk, amig a masik felveszi - kulon a csengohangtol. */
    ringback: ringbackSchema,
    holdMusic: holdMusicSchema,
});
export const updateNotificationPrefsInput = notificationPrefsSchema.partial().extend({
    keywords: z.array(z.string().min(1).max(40)).max(30).optional(),
    vipUserIds: z.array(z.string().min(1).max(64)).max(50).optional(),
});
export const DEFAULT_NOTIFICATION_PREFS = {
    globalLevel: 'mentions',
    afterHoursLevel: 'none',
    keywords: [],
    directAlways: true,
    vipUserIds: [],
    soundEnabled: true,
    soundVolume: 100,
    soundName: DEFAULT_SOUND,
    ringtone: DEFAULT_RINGTONE,
    ringback: DEFAULT_RINGBACK,
    holdMusic: DEFAULT_HOLD_MUSIC,
};
/** Az ertesites, ahogy a kliens megkapja a WebSocketen. */
export const notificationSchema = z.object({
    message: messageSchema,
    channelId: z.string(),
    channelName: z.string().nullable(),
    channelKind: z.string(),
    authorName: z.string(),
    reason: notifyReasonSchema,
    /** A felhasznalo kerte-e a hangjelzest. */
    sound: z.boolean(),
    /** Melyik hang szoljon - a felhasznalo valasztasa. */
    soundName: notificationSoundSchema,
});
// ─── Ertesitesi ablak ─────────────────────────────────────────────────────────
//
// A "mikor ertesitsunk" kerdest a felhasznalo MUNKAIDO-savja donti el
// (`presence-status.ts`). Itt csak az ertesites hangja es a szurok maradtak.
/**
 * Szerepel-e valamelyik kulcsszo a szovegben?
 *
 * Szohatarra illesztunk es ekezet-fuggetlenul hasonlitunk: aki az "oltas"
 * szora figyel, ne kapjon riasztast a "boltos" szotol, de az "oltást" hozza meg.
 */
export function matchesKeyword(text, keywords) {
    if (keywords.length === 0)
        return null;
    const fold = (value) => value
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
    const haystack = fold(text);
    for (const keyword of keywords) {
        const needle = fold(keyword.trim());
        if (!needle)
            continue;
        const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(needle)}([^\\p{L}\\p{N}]|$)`, 'u');
        if (pattern.test(haystack))
            return keyword;
    }
    return null;
}
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// ─── Diagnosztika: miert (nem) szol egy ertesites ─────────────────────────────
/**
 * Melyik szabaly dontott (lasd `explainNotification` a szerveren). A felulet
 * ebbol irja ki az emberi magyarazatot - a szoveg nem a szerverre tartozik.
 */
export const notificationRuleSchema = z.enum([
    /** A csatorna "soha" - meg az emlites sem. */
    'channel_never',
    /** Elrejtett csatorna: csak a szemelyes emlites szol. */
    'hidden',
    /** Elrejtett csatorna, de szemelyesen emlitettek. */
    'hidden_mention',
    /** Nemitott csatorna. */
    'muted',
    /** Nemitott csatorna, de az emlites atment. */
    'muted_mention',
    /** DM + "DM mindig". */
    'direct_always',
    /** VIP-nek jelolt szerzo / indito. */
    'vip',
    /** A csatorna "mindig". */
    'channel_always',
    /** Munkaidoben az eredo szint "semmi". */
    'level_none_working',
    /** Munkaidon kivul az eredo szint "semmi". */
    'level_none_after_hours',
    /** A szint engedi, es van "miert szolna" ok (emlites, kulcsszo, DM, oleles). */
    'direct_reason',
    /** A szint "minden". */
    'level_all',
    /** A szint "emlites + DM", sima csatornauzenetnel nincs mi szoljon. */
    'level_mentions_only',
]);
//# sourceMappingURL=notification.js.map